import { useEffect, useState } from 'react';
import {
  getApiErrorCode,
  getApiErrorDetails,
  getApiErrorMessage,
  transfersAPI,
  type StockShortageDto,
  type TransferDetailDto,
} from '@matix/api-client';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { useStockLevels } from '../../store/stockLevels';
import { XIcon } from '../../components/icons';
import { formatReceiptDateTime } from '../receipts/helpers';
import { printTransferSlip } from './transferPrint';
import { StatusChip, storeName, TILL_CANCELS_AND_TRANSFERS_NOW } from './shared';

/**
 * One transfer request: its lines, what is on hand at the source right now,
 * and the two actions that change it — Receive (destination only) and Cancel.
 * Both confirm inline rather than through window.confirm, so the cashier can
 * still read the lines while deciding.
 */
export function TransferDetailDialog(props: {
  id: number;
  /** Opened by saving it just now — print only, Receive waits for a re-open. */
  justCreated?: boolean;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const { t, lang } = useLang();
  const openShortageDialog = useStockLevels((s) => s.openDialog);
  const [detail, setDetail] = useState<TransferDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'receive' | 'cancel' | null>(null);
  const [confirm, setConfirm] = useState<'receive' | 'cancel' | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    transfersAPI
      .get(props.id)
      .then((d) => { if (alive) setDetail(d); })
      .catch((err) => { if (alive) toast(getApiErrorMessage(err), 'error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [props.id]);

  async function run(action: 'receive' | 'cancel'): Promise<void> {
    if (!detail) return;
    setBusy(action);
    try {
      const next = action === 'receive'
        ? await transfersAPI.receive(detail.id)
        : await transfersAPI.cancel(detail.id);
      setDetail(next);
      setConfirm(null);
      toast(
        action === 'receive'
          ? t({ en: `Transfer #${next.entry_no} received · goods document #${next.received_entry_no ?? '—'}`, ar: `تم استلام التحويل #${next.entry_no} · سند التحويل #${next.received_entry_no ?? '—'}` })
          : t({ en: `Transfer #${next.entry_no} cancelled`, ar: `تم إلغاء التحويل #${next.entry_no}` }),
        'success',
      );
      props.onChanged();
    } catch (err) {
      if (getApiErrorCode(err) === 'INSUFFICIENT_STOCK') {
        const details = getApiErrorDetails<{ shortages?: StockShortageDto[] }>(err);
        openShortageDialog(details?.shortages ?? [], true);
      } else {
        toast(getApiErrorMessage(err), 'error');
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="modal-overlay show" onClick={props.onClose}>
      <div className="modal tr-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-img" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>⇄</div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {t({ en: 'Store transfer', ar: 'تحويل مخزني' })}
              {detail && <> #{detail.entry_no}</>}
              {detail && <StatusChip status={detail.status} />}
            </div>
            {detail && (
              <div className="modal-header-name-ar">
                {storeName(detail.from_store, lang)} → {storeName(detail.to_store, lang)}
                {detail.created_at && <> · {formatReceiptDateTime(detail.created_at)}</>}
                {detail.created_by_name && <> · {detail.created_by_name}</>}
              </div>
            )}
          </div>
          <button type="button" className="modal-close" onClick={props.onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          {loading && <div className="stock-balance-hint">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>}
          {detail && (
            <>
              {(detail.reference_no || detail.notes || detail.received_entry_no != null) && (
                <div className="tr-detail-meta">
                  {detail.reference_no && <span><b>{t({ en: 'Reference', ar: 'المرجع' })}:</b> {detail.reference_no}</span>}
                  {detail.received_entry_no != null && (
                    <span>
                      <b>{t({ en: 'Goods document', ar: 'سند التحويل' })}:</b> #{detail.received_entry_no}
                      {detail.received_at && <> · {formatReceiptDateTime(detail.received_at)}</>}
                    </span>
                  )}
                  {detail.notes && <span><b>{t({ en: 'Notes', ar: 'ملاحظات' })}:</b> {detail.notes}</span>}
                </div>
              )}

              <div className="stock-balance-table-wrap">
                <table className="stock-balance-table tr-lines">
                  <thead>
                    <tr>
                      <th className="sb-num">#</th>
                      <th>{t({ en: 'Item', ar: 'الصنف' })}</th>
                      <th className="sb-qty">{t({ en: 'Quantity', ar: 'الكمية' })}</th>
                      <th>{t({ en: 'Unit', ar: 'الوحدة' })}</th>
                      {detail.status === 'pending' && (
                        <th className="sb-qty">{t({ en: 'At source now', ar: 'المتاح بالمصدر' })}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((l, i) => {
                      const short = l.available_at_source != null && l.available_at_source < l.qty;
                      return (
                        <tr key={l.id} className={short ? 'is-short' : undefined}>
                          <td className="sb-num">{i + 1}</td>
                          <td>
                            <div>{(lang === 'ar' ? l.name_ar : l.name) || l.name || l.name_ar || l.code}</div>
                            <div className="tr-line-code">{l.code}{l.notes && <> · {l.notes}</>}</div>
                          </td>
                          <td className="sb-qty">{fmtQty(l.qty)}</td>
                          <td>{(lang === 'ar' ? l.unit_name_ar : l.unit_name) || l.unit_name || '—'}</td>
                          {detail.status === 'pending' && (
                            <td className={`sb-qty${short ? ' is-short' : ''}`}>
                              {l.available_at_source == null ? '—' : fmtQty(l.available_at_source)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {detail.status === 'pending' && detail.lines.some((l) => l.available_at_source != null && l.available_at_source < l.qty) && (
                <div className="cc-warn tr-warn">
                  {t({
                    en: 'Some lines exceed what the source store holds right now. Receiving will be refused if this company does not allow negative stock.',
                    ar: 'بعض الأصناف تتجاوز رصيد المخزن المصدر حالياً. سيُرفض الاستلام إذا كانت الشركة لا تسمح بالرصيد السالب.',
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {detail && (
          <div className="modal-footer tr-detail-footer">
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => printTransferSlip(detail, lang, 'roll')}>
                🖨 {t({ en: 'Print', ar: 'طباعة' })}
              </button>
              <button type="button" className="btn-secondary" onClick={() => printTransferSlip(detail, lang, 'a4')}>
                {t({ en: 'Print A4', ar: 'طباعة A4' })}
              </button>
            </div>
            <div className="modal-actions">
              {TILL_CANCELS_AND_TRANSFERS_NOW && detail.can_cancel && confirm !== 'receive' && (
                confirm === 'cancel' ? (
                  <>
                    <button type="button" className="btn-secondary" onClick={() => setConfirm(null)} disabled={busy != null}>
                      {t({ en: 'Keep it', ar: 'إبقاؤه' })}
                    </button>
                    <button type="button" className="cc-btn cc-btn--danger" onClick={() => void run('cancel')} disabled={busy != null}>
                      {busy === 'cancel' ? t({ en: 'Cancelling…', ar: 'جارٍ الإلغاء…' }) : t({ en: 'Yes, cancel the request', ar: 'نعم، ألغِ الطلب' })}
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn-secondary tr-btn-danger" onClick={() => setConfirm('cancel')} disabled={busy != null}>
                    {t({ en: 'Cancel request', ar: 'إلغاء الطلب' })}
                  </button>
                )
              )}
              {detail.can_receive && !props.justCreated && confirm !== 'cancel' && (
                confirm === 'receive' ? (
                  <>
                    <button type="button" className="btn-secondary" onClick={() => setConfirm(null)} disabled={busy != null}>
                      {t({ en: 'Not yet', ar: 'ليس الآن' })}
                    </button>
                    <button type="button" className="btn-primary-modal" onClick={() => void run('receive')} disabled={busy != null}>
                      {busy === 'receive' ? t({ en: 'Receiving…', ar: 'جارٍ الاستلام…' }) : t({ en: 'Confirm: goods are here', ar: 'تأكيد: البضاعة وصلت' })}
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn-primary-modal" onClick={() => setConfirm('receive')} disabled={busy != null}>
                    ✓ {t({ en: 'Receive goods', ar: 'استلام البضاعة' })}
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}
